import { NODE_CONTEXT_IMPORT } from "./NodeContextImportConstants";
import {
  buildValidationResult,
  VALIDATION_VERDICT,
} from "./ContextImportValidationResult";

function lines() {
  return NODE_CONTEXT_IMPORT.statusLines;
}

export function buildImportStatusDisplay({
  totalNodes,
  createdNodes,
  updatedNodes,
  failedNodes,
  importStatus,
}) {
  const L = lines();
  return [
    `${L.totalNodes} ${totalNodes}`,
    `${L.createdNodes} ${createdNodes}`,
    `${L.updatedNodes} ${updatedNodes}`,
    `${L.failedNodes} ${failedNodes}`,
    `${L.importStatus} ${importStatus}`,
  ].join("\n");
}

export function formatValidateApiResponseToStatus(data, options = {}) {
  const validationResult = buildValidationResult({
    apiData: data,
    uploadedNodes: options.uploadedNodes ?? [],
    fileName: options.fileName ?? "",
    refusedReason: options.refusedReason ?? null,
  });

  if (!data || typeof data !== "object") {
    if (options.refusedReason) {
      return {
        isValid: false,
        tier: "error",
        validationResult,
        verdict: validationResult.verdict,
        canUpload: false,
      };
    }
    return {
      isValid: false,
      tier: "error",
      validationResult,
      verdict: VALIDATION_VERDICT.FAILED,
      canUpload: false,
    };
  }

  const tier = validationResult.verdict === VALIDATION_VERDICT.FAILED ? "error" : "success";

  return {
    isValid: validationResult.canUpload,
    tier,
    validationResult,
    verdict: validationResult.verdict,
    canUpload: validationResult.canUpload,
  };
}

export function extractJobPayload(data) {
  return data?.response ?? data;
}

export function extractContextImportJobId(data) {
  const job = extractJobPayload(data);
  const id = job?.jobId;
  if (id == null || String(id).trim() === "") {
    return null;
  }
  return id;
}

export function extractContextImportJobState(data) {
  const job = extractJobPayload(data);
  return String(job?.state ?? "").toUpperCase();
}

function formatResolvedSources(sources) {
  if (!Array.isArray(sources) || sources.length === 0) {
    return null;
  }
  const values = sources.filter((value) => typeof value === "string" && value);
  return values.length > 0 ? values.join(", ") : null;
}

function formatSourceNodeCounts(sourceNodeCounts) {
  if (sourceNodeCounts == null || Array.isArray(sourceNodeCounts)) {
    return null;
  }
  const entries = Object.entries(sourceNodeCounts).filter(([key]) => key);
  if (entries.length === 0) {
    return null;
  }
  return entries.map(([key, value]) => `${key}: ${Number(value)}`).join(", ");
}

function appendCompletedSourceMetrics(display, result, L) {
  const extraLines = [];
  const resolvedSources = formatResolvedSources(result.resolvedSources);
  if (resolvedSources) {
    extraLines.push(`${L.resolvedSources} ${resolvedSources}`);
  }
  const sourceNodeCounts = formatSourceNodeCounts(result.sourceNodeCounts);
  if (sourceNodeCounts) {
    extraLines.push(`${L.sourceNodeCounts} ${sourceNodeCounts}`);
  }
  if (
    result.contextOnlyNodes != null &&
    Number.isFinite(Number(result.contextOnlyNodes))
  ) {
    extraLines.push(`${L.contextOnlyNodes} ${Number(result.contextOnlyNodes)}`);
  }
  if (extraLines.length === 0) {
    return display;
  }
  return [display, ...extraLines].join("\n");
}

export function formatJobApiResponseToStatus(data) {
  const L = lines();
  const job = extractJobPayload(data);
  if (!job) {
    return {
      display: buildImportStatusDisplay({
        totalNodes: 0,
        createdNodes: 0,
        updatedNodes: 0,
        failedNodes: 0,
        importStatus: L.importFailed,
      }),
      success: false,
      tier: "error",
      errorMessage: null,
    };
  }

  const result = job.result ?? {};
  const stateUpper = String(job.state ?? "").toUpperCase();
  const success = stateUpper === "COMPLETED";
  const importStatus = success ? L.importSuccessful : L.importFailed;
  const errorMessage = job.errorMessage ?? result.errorMessage ?? null;
  const totalNodes =
    result.totalNodes != null
      ? Number(result.totalNodes)
      : Number(result.nodeCount);
  const baseDisplay = buildImportStatusDisplay({
    totalNodes,
    createdNodes: Number(result.nodesCreated),
    updatedNodes: Number(result.nodesUpdated),
    failedNodes: Number(result.failedNodes ?? 0),
    importStatus,
  });
  const display = success
    ? appendCompletedSourceMetrics(baseDisplay, result, L)
    : baseDisplay;

  return {
    display,
    success,
    tier: success ? "success" : "error",
    errorMessage,
    resolvedSources: result.resolvedSources ?? null,
    sourceNodeCounts: result.sourceNodeCounts ?? null,
    contextOnlyNodes:
      result.contextOnlyNodes != null
        ? Number(result.contextOnlyNodes)
        : null,
  };
}
